// @givmo/auth — adding a passkey to the SIGNED-IN account (AS-WEB11; D-CBG-17 decision 5 reversed
// AS-WEB2's "consumers enrol on mobile").
//
//   POST /users/me/passkeys/registration/options  →  navigator.credentials.create()  →
//   POST /users/me/passkeys/registration           →  the new passkey, stored
//
// Both routes are authenticated and bound to the caller: the options name the signed-in account as
// the credential's user, and the finish stores the credential on that account. No session changes —
// unlike the sign-in and the re-confirmation, nothing here calls `signInWithCustomToken`.
//
// ⚠ THE FACTOR RULES ARE THE BACKEND'S, AND THEIR REFUSALS MUST REACH THE STEP-UP GATE UNTOUCHED.
// Rule 1 refuses an add on a session that is not recent enough (`step_up_required`, action
// `add_passkey`) or, for an account that already holds a passkey, one that did not confirm a passkey
// (`passkey_confirmation_required`). The caller runs this through `@givmo/step-up`'s gate, which
// answers those refusals with the "Confirm it's you" sheet and retries; the gate recognises them by
// their machine code on the api-client's error. So a TRANSPORT failure is rethrown exactly as the
// transport raised it — never wrapped — and only the browser's own ceremony is mapped onto this
// package's error model.
//
// Never fabricates a name: a passkey is stored unnamed unless the person names it (the server does
// not know which device answered, and a guessed "iPhone" for a security key would be a false claim
// on a security screen). Names are the person's, through Rename.

import { AuthError } from './errors';
import {
  isWebAuthnSupported,
  serializeAttestation,
  toCreationOptions,
  type AttestationCredentialLike,
  type PasskeyAttestationJson,
  type WebAuthnScope,
} from './webauthn';

/** Shown when the browser cannot run the ceremony at all. */
const PASSKEYS_UNAVAILABLE_MESSAGE = 'Passkeys are not available in this browser.';

/**
 * The two authenticated routes, injected as a port. A refusal from either is rethrown AS RAISED
 * (see the header): the step-up gate reads it.
 */
export interface PasskeyRegistrationTransport {
  /** `POST /users/me/passkeys/registration/options` → the raw `PublicKeyCredentialCreationOptionsJSON`. */
  requestRegistrationOptions(init?: { signal?: AbortSignal }): Promise<unknown>;
  /** `POST /users/me/passkeys/registration` with the created credential. */
  finishRegistration(
    attestation: PasskeyAttestationJson,
    init?: { signal?: AbortSignal },
  ): Promise<void>;
}

export interface PasskeyRegistrationOptions {
  /** Defaults to `navigator.credentials`. */
  credentials?: Pick<CredentialsContainer, 'create'>;
  /** Defaults to `globalThis`. */
  scope?: WebAuthnScope;
  /** Aborts the ceremony; surfaces as `cancelled`. */
  signal?: AbortSignal;
}

/** Whether this browser can create a passkey at all — a property read, no request. */
export function isPasskeyRegistrationSupported(scope: WebAuthnScope = globalThis): boolean {
  return isWebAuthnSupported(scope);
}

/**
 * Create a passkey for the signed-in account and store it.
 *
 * Rejects with:
 * - the transport's own error, untouched, when either route refuses — the factor rules' refusals
 *   among them, for the step-up gate to answer;
 * - an {@link AuthError} `cancelled` when the person dismissed the browser's prompt (or it was
 *   aborted, or the device already holds a passkey for this account — WebAuthn reports all three
 *   alike), and `passkeyCeremonyFailed` for any other ceremony failure.
 */
export async function registerPasskey(
  transport: PasskeyRegistrationTransport,
  options: PasskeyRegistrationOptions = {},
): Promise<void> {
  const scope = options.scope ?? globalThis;
  const credentials = options.credentials ?? globalThis.navigator?.credentials;
  if (!isWebAuthnSupported(scope) || credentials === undefined) {
    throw new AuthError('passkeyCeremonyFailed', { message: PASSKEYS_UNAVAILABLE_MESSAGE });
  }

  // A transport refusal propagates as raised (see the header).
  const document = await transport.requestRegistrationOptions({ signal: options.signal });

  let creationOptions: PublicKeyCredentialCreationOptions;
  try {
    creationOptions = toCreationOptions(document, scope);
  } catch {
    throw new AuthError('passkeyCeremonyFailed');
  }

  let credential: AttestationCredentialLike | null;
  try {
    credential = (await credentials.create({
      publicKey: creationOptions,
      signal: options.signal,
    })) as AttestationCredentialLike | null;
  } catch (error) {
    const name =
      typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : '';
    // `InvalidStateError` is the authenticator already holding one of `excludeCredentials`: nothing
    // was created, and the person has a passkey on this device already — not a failure to report.
    if (name === 'NotAllowedError' || name === 'AbortError' || name === 'InvalidStateError') {
      throw new AuthError('cancelled');
    }
    throw new AuthError('passkeyCeremonyFailed');
  }
  if (credential === null) throw new AuthError('cancelled');

  await transport.finishRegistration(serializeAttestation(credential), { signal: options.signal });
}
